import { useEffect, useRef, useState } from "react"
import { useLingui } from "@lingui/react/macro"

// Timeline search field for the deal/contact activity feed
// (CON-146, Paper "CRM - Search"). A real text input that
// debounces a server-side full-text search via the
// `activity_search` param.
//
// THE SEAM: this component owns the input's controlled VALUE
// (seeded once from `initialValue`) and the debounce timer;
// the surface owns the actual reload via `onSearch`. Keeping
// the value here means it survives the partial reload — the
// `ActivityFeed` doesn't remount when its `items` prop swaps,
// so the input keeps both its focus and its text. The server
// is NOT the source of truth for the input text; the user's
// keystrokes are. We seed from `initialValue` only on first
// mount (e.g. landing on a URL that already carries
// `?activity_search=…`).
//
// ⌘F / Ctrl+F focuses the input (preventDefault) so the
// keyboard shortcut targets the timeline search instead of
// the browser's find-in-page.

const DEBOUNCE_MS = 275

interface TimelineSearchFieldProps {
  // Initial text — read once on mount to hydrate the input
  // from the URL's `activity_search` param. Subsequent prop
  // changes are ignored on purpose (the input is the source
  // of truth once mounted).
  initialValue?: string
  // Fires after the debounce window with the current trimmed-
  // for-emptiness value. The surface turns this into a partial
  // reload (with `activity_search` when non-empty; without it
  // when empty, restoring the default merge-on-read feed).
  onSearch: (value: string) => void
}

export function TimelineSearchField({
  initialValue = "",
  onSearch,
}: TimelineSearchFieldProps) {
  const { t } = useLingui()
  const [value, setValue] = useState(initialValue)
  const inputRef = useRef<HTMLInputElement>(null)

  // Keep the latest onSearch in a ref so the debounce effect
  // doesn't re-fire (and reset the timer) just because the
  // surface handed us a fresh closure on re-render.
  const onSearchRef = useRef(onSearch)
  useEffect(() => {
    onSearchRef.current = onSearch
  }, [onSearch])

  // The value the surface last saw, so we don't fire a reload
  // for a value that already matches the server state — e.g.
  // the debounce settling on the same text after a focus
  // round-trip, or the initial mount value.
  const lastSearchedRef = useRef(initialValue)

  useEffect(() => {
    if (value === lastSearchedRef.current) return
    const handle = window.setTimeout(() => {
      lastSearchedRef.current = value
      onSearchRef.current(value)
    }, DEBOUNCE_MS)
    return () => window.clearTimeout(handle)
  }, [value])

  // ⌘F / Ctrl+F focuses the timeline search instead of the
  // browser find bar. Window-level so it works wherever the
  // feed is mounted (page, drawer).
  useEffect(() => {
    const handle = (event: KeyboardEvent) => {
      const isFind =
        (event.metaKey || event.ctrlKey) &&
        !event.shiftKey &&
        !event.altKey &&
        (event.key === "f" || event.key === "F")
      if (!isFind) return
      event.preventDefault()
      inputRef.current?.focus()
    }
    window.addEventListener("keydown", handle)
    return () => window.removeEventListener("keydown", handle)
  }, [])

  return (
    <div
      data-testid="timeline-search"
      className="flex items-center rounded-[10px] py-2.5 px-3 gap-2 bg-[#ECE7DE]"
    >
      <SearchIcon />
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(event) => setValue(event.target.value)}
        aria-label={t`Search timeline`}
        placeholder={t`Search messages, notes & calls…`}
        className="grow min-w-0 bg-transparent text-sm leading-5 text-[#1A1917] placeholder:text-[#9A938A] focus:outline-none"
      />
      <KbdHint />
    </div>
  )
}

function SearchIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className="shrink-0"
    >
      <circle cx="11" cy="11" r="7" stroke="#9A938A" strokeWidth="1.8" />
      <line
        x1="20.5"
        y1="20.5"
        x2="16.5"
        y2="16.5"
        stroke="#9A938A"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  )
}

function KbdHint() {
  return (
    <span
      aria-hidden="true"
      className="flex items-center rounded-[5px] py-0.5 px-1.5 bg-[#E2DCD2] shrink-0"
    >
      <span className="font-medium text-[#9A938A] text-[11px] leading-3">
        ⌘F
      </span>
    </span>
  )
}
